import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import { CustomCSSLoader } from "@/components/CustomCSSLoader";
import { DynamicFavicon } from "@/components/DynamicFavicon";
import { GTMLoader } from "@/components/GTMLoader";
import { CartProvider } from "@/contexts/CartContext";
import PageTransition from "@/components/PageTransition";
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import AIUsage from "@/pages/AIUsage";
import ReturnPolicy from "@/pages/ReturnPolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import CheckoutRedirect from "@/pages/CheckoutRedirect";
import Cart from "@/pages/Cart";

// Lazy load Admin to reduce initial bundle size (TipTap editor is heavy)
const Admin = lazy(() => import("@/pages/Admin"));

// Loading fallback for admin routes
const AdminFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

// Root layout with providers
function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <PageTransition><Index /></PageTransition> },
      { path: "/about", element: <PageTransition><About /></PageTransition> },
      { path: "/contact", element: <PageTransition><Contact /></PageTransition> },
      { path: "/login", element: <PageTransition><AdminLogin /></PageTransition> },
      { path: "/admin", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/games", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/games/:id", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/news", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/news/:id", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/crew", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/crew/:id", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/leads", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/pages", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/pages/:pageKey", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/admin/custom", element: <Suspense fallback={<AdminFallback />}><Admin /></Suspense> },
      { path: "/games", element: <PageTransition><Games /></PageTransition> },
      { path: "/games/:slug", element: <PageTransition><GameDetail /></PageTransition> },
      { path: "/news", element: <PageTransition><News /></PageTransition> },
      { path: "/news/:slug", element: <PageTransition><NewsArticle /></PageTransition> },
      { path: "/ai-usage", element: <PageTransition><AIUsage /></PageTransition> },
      { path: "/return-policy", element: <PageTransition><ReturnPolicy /></PageTransition> },
      { path: "/privacy-policy", element: <PageTransition><PrivacyPolicy /></PageTransition> },
      { path: "/terms-of-use", element: <PageTransition><TermsOfUse /></PageTransition> },
      { path: "/cart", element: <PageTransition><Cart /></PageTransition> },
      { path: "/checkout/redirect", element: <CheckoutRedirect /> },
      { path: "*", element: <PageTransition><NotFound /></PageTransition> },
    ],
  },
]);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <SiteSettingsProvider>
        <CartProvider>
          <TooltipProvider>
            <CustomCSSLoader />
            <DynamicFavicon />
            <GTMLoader />
            <Toaster />
            <Sonner position="bottom-left" />
            <RouterProvider router={router} />
          </TooltipProvider>
        </CartProvider>
      </SiteSettingsProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
